import { useCallback, useEffect, useMemo, useState } from "react";
import client from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useFinancialYear } from "../context/FinancialYearContext";

const emptyLine = { itemId: "", hsnCode: "", qty: 1, rate: 0, gstPercent: 18 };

const fmt = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const get = (obj, a, b) => obj?.[a] ?? obj?.[b];

const styles = {
  page: {
    fontSize: 11,
    height: "calc(100vh - 20px)",
    overflowY: "auto",
    overflowX: "hidden",
    padding: 0,
  },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "8px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: { fontSize: 13, fontWeight: 800, margin: 0 },
  sub: { fontSize: 10, opacity: 0.8 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: {
    height: 28,
    fontSize: 11,
    borderRadius: 7,
    fontWeight: 600,
    padding: "3px 9px",
  },
  info: {
    fontSize: 11,
    padding: "7px 10px",
    borderRadius: 10,
    background: "#fff8e1",
    border: "1px solid #ffe082",
  },
  tableWrap: {
    maxHeight: 300,
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 1050, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
    background: "#07111f",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "5px 6px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  summaryTh: {
    fontSize: 11,
    padding: "6px 8px",
    background: "#07111f",
    color: "#fff",
  },
  summaryTd: { fontSize: 11, padding: "6px 8px" },
};

export default function DebitNotePage() {
  const { user } = useAuth();
  const { financialYear } = useFinancialYear();

  const companyId = Number(
    user?.companyId || localStorage.getItem("companyId") || 0,
  );

  const fyFrom = financialYear?.fromDate;
  const fyTo = financialYear?.toDate;
  const fyLocked = Boolean(financialYear?.isLocked || financialYear?.IsLocked);

  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [purchaseLoadMsg, setPurchaseLoadMsg] = useState("");
  const [dateLocked, setDateLocked] = useState(false);
  const [checkingDateLock, setCheckingDateLock] = useState(false);

  const [form, setForm] = useState({
    voucherNo: "",
    voucherDate: fyFrom || new Date().toISOString().split("T")[0],
    supplierId: "",
    purchaseId: "",
    purchaseNo: "",
    returnType: "CREDIT",
    supplyType: "INTRA",
    discountAmount: 0,
    notes: "",
    details: [{ ...emptyLine }],
  });

  useEffect(() => {
    loadMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fyFrom) {
      setForm((p) => ({
        ...p,
        voucherDate: fyFrom,
      }));
    }
  }, [fyFrom]);

  const selectedDateOutsideFy =
    form.voucherDate && fyFrom && fyTo
      ? form.voucherDate < fyFrom || form.voucherDate > fyTo
      : false;

  const isPostingBlocked =
    fyLocked || dateLocked || selectedDateOutsideFy || checkingDateLock;

  useEffect(() => {
    checkVoucherDateLock(form.voucherDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.voucherDate, companyId]);

  const checkVoucherDateLock = async (voucherDate) => {
    if (!voucherDate || !companyId) {
      setDateLocked(false);
      return;
    }

    try {
      setCheckingDateLock(true);

      const res = await client.get("/finance/is-date-locked", {
        params: {
          entryDate: voucherDate,
          companyId,
        },
      });

      setDateLocked(Boolean(res.data?.isLocked));
    } catch (err) {
      console.error("Date lock check failed", err);
      setDateLocked(false);
    } finally {
      setCheckingDateLock(false);
    }
  };

  const loadPurchases = async (companyIdValue) => {
    const urls = [
      `/purchase-vouchers?companyId=${companyIdValue}`,
      `/purchases?companyId=${companyIdValue}`,
      `/vouchers/purchase?companyId=${companyIdValue}`,
      `/vouchers/purchases?companyId=${companyIdValue}`,
      `/purchase?companyId=${companyIdValue}`,
    ];

    for (const url of urls) {
      try {
        const res = await client.get(url);
        const data = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
            ? res.data.data
            : [];

        if (data.length > 0) {
          setPurchaseLoadMsg("");
          return data;
        }
      } catch {
        // try next route
      }
    }

    setPurchaseLoadMsg(
      "Purchases not loaded. Please confirm your purchase list API route.",
    );
    return [];
  };

  const loadMasters = async () => {
    try {
      setLoading(true);

      const [supplierRes, itemRes] = await Promise.all([
        client.get("/suppliers"),
        client.get("/inventory/items"),
      ]);

      setSuppliers(Array.isArray(supplierRes.data) ? supplierRes.data : []);
      setItems(Array.isArray(itemRes.data) ? itemRes.data : []);
      setPurchases(await loadPurchases(companyId));
    } catch (e) {
      console.error(e);
      alert("Failed to load suppliers/items");
    } finally {
      setLoading(false);
    }
  };

  const calculateLine = useCallback(
    (line) => {
      const qty = Number(line?.qty || 0);
      const rate = Number(line?.rate || 0);
      const gstPercent = Number(line?.gstPercent || 0);
      const taxableValue = qty * rate;
      const gstAmount = (taxableValue * gstPercent) / 100;

      return {
        taxableValue,
        cgst: form.supplyType === "INTRA" ? gstAmount / 2 : 0,
        sgst: form.supplyType === "INTRA" ? gstAmount / 2 : 0,
        igst: form.supplyType === "INTER" ? gstAmount : 0,
        lineTotal: taxableValue + gstAmount,
      };
    },
    [form.supplyType],
  );

  const selectPurchase = (purchaseId) => {
    const pur = purchases.find(
      (x) => String(get(x, "Id", "id")) === String(purchaseId),
    );
    setSelectedPurchase(pur || null);

    if (!pur) {
      setForm((p) => ({ ...p, purchaseId: "", purchaseNo: "" }));
      return;
    }

    const purNo =
      get(pur, "PurchaseNo", "purchaseNo") ||
      get(pur, "VoucherNo", "voucherNo") ||
      get(pur, "InvoiceNo", "invoiceNo") ||
      "";

    setForm((p) => ({
      ...p,
      purchaseId: get(pur, "Id", "id"),
      purchaseNo: purNo,
      supplierId: get(pur, "SupplierId", "supplierId") || p.supplierId,
      supplyType: get(pur, "SupplyType", "supplyType") || "INTRA",
      notes: p.notes || `Debit note against purchase ${purNo}`,
    }));
  };

  const updateLine = (index, field, value) => {
    const details = [...form.details];
    details[index] = { ...details[index], [field]: value };

    if (field === "itemId") {
      const item = items.find(
        (x) => String(get(x, "Id", "id")) === String(value),
      );
      if (item) {
        details[index].hsnCode = get(item, "HSNCode", "hsnCode") || "";
        details[index].rate = Number(
          get(item, "CostPrice", "costPrice") ??
            get(item, "PurchasePrice", "purchasePrice") ??
            0,
        );
        details[index].gstPercent = Number(
          get(item, "GSTPercent", "gstPercent") ?? 18,
        );
      }
    }

    setForm((p) => ({ ...p, details }));
  };

  const addRow = () => {
    if (isPostingBlocked) return;

    setForm((p) => ({ ...p, details: [...p.details, { ...emptyLine }] }));
  };

  const removeRow = (index) => {
    if (isPostingBlocked) return;

    if (form.details.length === 1) return alert("At least one row is required");

    setForm((p) => ({
      ...p,
      details: p.details.filter((_, i) => i !== index),
    }));
  };

  const totals = useMemo(() => {
    return form.details.reduce(
      (acc, line) => {
        const c = calculateLine(line);
        acc.subTotal += c.taxableValue;
        acc.cgst += c.cgst;
        acc.sgst += c.sgst;
        acc.igst += c.igst;
        acc.taxAmount += c.cgst + c.sgst + c.igst;
        acc.grandTotal += c.lineTotal;
        return acc;
      },
      { subTotal: 0, cgst: 0, sgst: 0, igst: 0, taxAmount: 0, grandTotal: 0 },
    );
  }, [form.details, calculateLine]);

  const grandTotal = totals.grandTotal - Number(form.discountAmount || 0);

  const save = async () => {
    if (checkingDateLock)
      return alert("Please wait. Checking financial year lock status.");

    if (fyLocked)
      return alert("Selected financial year is locked. Debit note cannot be posted.");

    if (dateLocked)
      return alert("Selected voucher date belongs to locked financial year.");

    if (selectedDateOutsideFy)
      return alert("Voucher date must be within the selected financial year.");

    if (!companyId)
      return alert("Company is required. Please login again.");

    if (!form.voucherNo.trim()) return alert("Voucher No is required");
    if (!form.supplierId) return alert("Supplier is required");

    for (let i = 0; i < form.details.length; i++) {
      const row = form.details[i];
      if (!row.itemId) return alert(`Select item in row ${i + 1}`);
      if (Number(row.qty || 0) <= 0)
        return alert(`Qty must be greater than 0 in row ${i + 1}`);
      if (Number(row.rate || 0) <= 0)
        return alert(`Rate must be greater than 0 in row ${i + 1}`);
    }

    const payload = {
      companyId,
      voucherNo: form.voucherNo.trim(),
      voucherDate: form.voucherDate,
      supplierId: Number(form.supplierId),
      purchaseId: selectedPurchase
        ? Number(get(selectedPurchase, "Id", "id"))
        : null,
      purchaseNo: selectedPurchase
        ? get(selectedPurchase, "PurchaseNo", "purchaseNo") ||
          get(selectedPurchase, "VoucherNo", "voucherNo") ||
          get(selectedPurchase, "InvoiceNo", "invoiceNo")
        : form.purchaseNo,
      returnType: form.returnType,
      supplyType: form.supplyType,
      discountAmount: Number(form.discountAmount || 0),
      notes: form.notes,
      details: form.details.map((x) => ({
        itemId: Number(x.itemId),
        hsnCode: x.hsnCode,
        qty: Number(x.qty),
        rate: Number(x.rate),
        gstPercent: Number(x.gstPercent),
      })),
    };

    try {
      setSaving(true);
      await client.post("/vouchers/debit-note", payload);
      alert("Debit note saved successfully");

      setSelectedPurchase(null);
      setForm({
        voucherNo: "",
        voucherDate: fyFrom || new Date().toISOString().split("T")[0],
        supplierId: "",
        purchaseId: "",
        purchaseNo: "",
        returnType: "CREDIT",
        supplyType: "INTRA",
        discountAmount: 0,
        notes: "",
        details: [{ ...emptyLine }],
      });

      loadMasters();
    } catch (e) {
      console.error(e);
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.title ||
          "Failed to save debit note",
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedSupplier = suppliers.find(
    (s) => String(get(s, "Id", "id")) === String(form.supplierId),
  );

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div>
            <h6 style={styles.title}>
              <i className="bi bi-arrow-return-right me-2"></i>
              Debit Note / Purchase Return
            </h6>
            <div style={styles.sub}>
              Link debit note with purchase and reverse GST input
            </div>
          </div>

          <span
            className={`badge ${isPostingBlocked ? "bg-danger" : "bg-dark"}`}
            style={{ fontSize: 10 }}
          >
            {checkingDateLock
              ? "Checking FY"
              : isPostingBlocked
                ? "Posting Blocked"
                : form.supplyType === "INTRA"
                  ? "CGST + SGST"
                  : "IGST"}
          </span>
        </div>

        <div style={styles.body}>
          {loading && (
            <div className="alert alert-info py-2" style={{ fontSize: 11 }}>
              Loading masters...
            </div>
          )}

          {checkingDateLock && (
            <div className="alert alert-info py-2" style={{ fontSize: 11 }}>
              Checking financial year lock status...
            </div>
          )}

          {fyLocked && (
            <div className="alert alert-danger py-2" style={{ fontSize: 11 }}>
              <i className="bi bi-lock-fill me-1"></i>
              Selected financial year is locked. Debit note posting is blocked.
            </div>
          )}

          {dateLocked && (
            <div className="alert alert-danger py-2" style={{ fontSize: 11 }}>
              <i className="bi bi-lock-fill me-1"></i>
              Selected voucher date belongs to locked financial year.
            </div>
          )}

          {selectedDateOutsideFy && (
            <div className="alert alert-warning py-2" style={{ fontSize: 11 }}>
              <i className="bi bi-exclamation-triangle me-1"></i>
              Voucher date must be between {fyFrom} and {fyTo}.
            </div>
          )}

          {purchaseLoadMsg && (
            <div className="alert alert-warning py-2" style={{ fontSize: 11 }}>
              {purchaseLoadMsg}
            </div>
          )}

          <div className="row g-2 mb-2">
            <Field label="Voucher No" className="col-md-2">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.voucherNo}
                disabled={isPostingBlocked}
                onChange={(e) =>
                  setForm({ ...form, voucherNo: e.target.value })
                }
              />
            </Field>

            <Field label="Date" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                min={fyFrom || undefined}
                max={fyTo || undefined}
                value={form.voucherDate}
                onChange={(e) =>
                  setForm({ ...form, voucherDate: e.target.value })
                }
              />
            </Field>

            <Field label="Supplier" className="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.supplierId}
                disabled={isPostingBlocked}
                onChange={(e) =>
                  setForm({
                    ...form,
                    supplierId: e.target.value,
                    purchaseId: "",
                    purchaseNo: "",
                  })
                }
              >
                <option value="">Select Supplier</option>
                {suppliers.map((s) => (
                  <option key={get(s, "Id", "id")} value={get(s, "Id", "id")}>
                    {(get(s, "SupplierCode", "supplierCode") || "") +
                      " - " +
                      (get(s, "SupplierName", "supplierName") || "")}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Against Purchase" className="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.purchaseId}
                disabled={isPostingBlocked}
                onChange={(e) => selectPurchase(e.target.value)}
              >
                <option value="">Select Purchase</option>
                {purchases
                  .filter(
                    (x) =>
                      !form.supplierId ||
                      String(get(x, "SupplierId", "supplierId")) ===
                        String(form.supplierId),
                  )
                  .map((x) => {
                    const purNo =
                      get(x, "PurchaseNo", "purchaseNo") ||
                      get(x, "VoucherNo", "voucherNo") ||
                      get(x, "InvoiceNo", "invoiceNo") ||
                      "";
                    return (
                      <option
                        key={get(x, "Id", "id")}
                        value={get(x, "Id", "id")}
                      >
                        {purNo +
                          " - ₹ " +
                          fmt(
                            get(x, "GrandTotal", "grandTotal") ||
                              get(x, "Amount", "amount") ||
                              0,
                          )}
                      </option>
                    );
                  })}
              </select>
            </Field>

            <Field label="Supply Type" className="col-md-2">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.supplyType}
                disabled={isPostingBlocked}
                onChange={(e) =>
                  setForm({ ...form, supplyType: e.target.value })
                }
              >
                <option value="INTRA">INTRA</option>
                <option value="INTER">INTER</option>
              </select>
            </Field>
          </div>

          <div style={styles.info} className="mb-2">
            <strong>Supplier:</strong>{" "}
            {get(selectedSupplier, "SupplierName", "supplierName") || "-"} |{" "}
            <strong>Purchase:</strong> {form.purchaseNo || "-"} |{" "}
            <strong>Return Type:</strong> {form.returnType}
          </div>

          <VoucherItemsTable
            items={items}
            rows={form.details}
            updateLine={updateLine}
            removeRow={removeRow}
            calculateLine={calculateLine}
            disabled={isPostingBlocked}
          />

          <button
            className="btn btn-outline-primary btn-sm mb-2"
            style={styles.btn}
            onClick={addRow}
            disabled={isPostingBlocked}
          >
            <i className="bi bi-plus-circle me-1"></i>Add Row
          </button>

          <VoucherFooter
            form={form}
            setForm={setForm}
            totals={totals}
            grandTotal={grandTotal}
            disabled={isPostingBlocked}
          />

          <button
            className="btn btn-success btn-sm"
            style={{ ...styles.btn, height: 32, minWidth: 130 }}
            onClick={save}
            disabled={saving || isPostingBlocked}
          >
            <i className="bi bi-check2-circle me-1"></i>
            {saving ? "Saving..." : "Save Debit Note"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function VoucherItemsTable({
  items,
  rows,
  updateLine,
  removeRow,
  calculateLine,
  disabled,
}) {
  return (
    <div className="mb-2" style={styles.tableWrap}>
      <table
        className="table table-bordered table-hover align-middle"
        style={styles.table}
      >
        <thead>
          <tr>
            {[
              ["Item", 190],
              ["HSN", 90],
              ["Qty", 70],
              ["Rate", 80],
              ["GST %", 70],
              ["Taxable", 90],
              ["CGST", 80],
              ["SGST", 80],
              ["IGST", 80],
              ["Total", 90],
              ["Action", 70],
            ].map(([h, w]) => (
              <th
                key={h}
                style={{ ...styles.th, minWidth: w }}
                className={
                  ["Taxable", "CGST", "SGST", "IGST", "Total"].includes(h)
                    ? "text-end"
                    : ""
                }
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, i) => {
            const c = calculateLine(row);

            return (
              <tr key={i}>
                <td style={styles.td}>
                  <select
                    className="form-select form-select-sm"
                    style={styles.input}
                    value={row.itemId}
                    disabled={disabled}
                    onChange={(e) => updateLine(i, "itemId", e.target.value)}
                  >
                    <option value="">Select Item</option>
                    {items.map((item) => (
                      <option
                        key={get(item, "Id", "id")}
                        value={get(item, "Id", "id")}
                      >
                        {(get(item, "ItemCode", "itemCode") || "") +
                          " - " +
                          (get(item, "ItemName", "itemName") || "")}
                      </option>
                    ))}
                  </select>
                </td>

                {["hsnCode", "qty", "rate", "gstPercent"].map((field) => (
                  <td style={styles.td} key={field}>
                    <input
                      type={
                        ["qty", "rate", "gstPercent"].includes(field)
                          ? "number"
                          : "text"
                      }
                      className="form-control form-control-sm"
                      style={styles.input}
                      value={row[field]}
                      disabled={disabled}
                      onChange={(e) => updateLine(i, field, e.target.value)}
                    />
                  </td>
                ))}

                <td className="text-end" style={styles.td}>
                  {fmt(c.taxableValue)}
                </td>
                <td className="text-end" style={styles.td}>
                  {fmt(c.cgst)}
                </td>
                <td className="text-end" style={styles.td}>
                  {fmt(c.sgst)}
                </td>
                <td className="text-end" style={styles.td}>
                  {fmt(c.igst)}
                </td>
                <td className="text-end fw-bold" style={styles.td}>
                  {fmt(c.lineTotal)}
                </td>

                <td style={styles.td}>
                  <button
                    className="btn btn-danger btn-sm"
                    style={{ ...styles.btn, height: 25, padding: "2px 8px" }}
                    disabled={disabled}
                    onClick={() => removeRow(i)}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function VoucherFooter({ form, setForm, totals, grandTotal, disabled }) {
  return (
    <div className="row g-2 mb-2">
      <Field label="Discount" className="col-md-3">
        <input
          type="number"
          className="form-control form-control-sm"
          style={styles.input}
          value={form.discountAmount}
          disabled={disabled}
          onChange={(e) => setForm({ ...form, discountAmount: e.target.value })}
        />
      </Field>

      <Field label="Notes" className="col-md-5">
        <input
          className="form-control form-control-sm"
          style={styles.input}
          value={form.notes}
          disabled={disabled}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Example: Debit note against purchase return / damaged item / rate difference"
        />
      </Field>

      <div className="col-md-4">
        <table className="table table-bordered mb-0">
          <tbody>
            {[
              ["Sub Total", totals.subTotal],
              ["CGST", totals.cgst],
              ["SGST", totals.sgst],
              ["IGST", totals.igst],
              ["Total GST", totals.taxAmount],
              ["Discount", form.discountAmount],
              ["Grand Total", grandTotal],
            ].map(([k, v]) => (
              <tr
                key={k}
                className={k === "Grand Total" ? "table-light fw-bold" : ""}
              >
                <th style={styles.summaryTh}>{k}</th>
                <td className="text-end" style={styles.summaryTd}>
                  ₹ {fmt(v)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}